import { cn } from '@/lib/utils'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Tab } from '@mui/material'
import { createContext, useState } from 'react'
import Dialog from '.'
import { Actions } from './DialogGuideActions'
import { DialogContentProps } from './DialogGuideContent'
import { DialogRootProps } from './DialogGuideRoot'
import { DialogTitleProps } from './DialogGuideTitle'

interface ITab {
    name: string
    label: string
    content: React.ReactNode
}

interface TabsContextProps {
    tabs: ITab[]
    defaultTab?: string
    onTabChange?: (tab: string) => any
}

type TabbedDialogProps = DialogRootProps & TabsContextProps

const TabbedDialogContext = createContext<{
    tabs: ITab[]
    onChange: (tab: string) => any
}>(null!)

function TabbedRoot({
    tabs,
    defaultTab,
    title,
    onTabChange,
    children,
    width = '50rem',
    ...props
}: TabbedDialogProps) {
    const [tab, setTab] = useState(defaultTab || tabs[0].name)

    const onChange = (tab: string) => {
        setTab(tab)
        onTabChange?.(tab)
    }

    return (
        <TabbedDialogContext.Provider value={{ tabs, onChange }}>
            <TabContext value={tab}>
                <Dialog.Root {...props} width={width}>
                    {children}
                </Dialog.Root>
            </TabContext>
        </TabbedDialogContext.Provider>
    )
}

function TabbedHeader({ children, ...props }: DialogTitleProps) {
    return (
        <TabbedDialogContext.Consumer>
            {({ tabs, onChange }) => (
                <Dialog.Title
                    {...props}
                    className={cn('pb-0 min-h-fit', props.className)}
                >
                    {children}
                    <TabList
                        onChange={(_, v: string) => onChange(v)}
                        className="mb-0"
                    >
                        {tabs.map(({ name, label }, index) => (
                            <Tab key={index} label={label} value={name} />
                        ))}
                    </TabList>
                </Dialog.Title>
            )}
        </TabbedDialogContext.Consumer>
    )
}

function TabbedContent({ children, ...props }: DialogContentProps) {
    return (
        <TabbedDialogContext.Consumer>
            {({ tabs }) =>
                tabs.map(({ name, content }, index) => (
                    <TabPanel
                        key={index}
                        value={name}
                        style={{
                            maxHeight: '100vh',
                            overflowY: 'auto',
                            padding: 0,
                        }}
                    >
                        <Dialog.Content {...props}>
                            {children}
                            {content}
                        </Dialog.Content>
                    </TabPanel>
                ))
            }
        </TabbedDialogContext.Consumer>
    )
}

/**
 * @example
 * import TabbedDialog from 'global/guides/Dialog/TabbedDialog'
 * 
    <TabbedDialog.Root
        tabs={[
            {
                name: '1',
                label: 'One',
                content: <FirstTab />,
            },
            {
                name: '1',
                label: 'Two',
                content: <SecondTab />,
            },
        ]}
        onSubmit={handleSubmit}
        loading={isLoading}
        schema={schema}
    >
        <TabbedDialog.Title>Dialog title</TabbedDialog.Title>
        <TabbedDialog.Content dividers />
        <TabbedDialog.Actions>
            <TabbedDialog.Actions.SecondaryButton />
            <TabbedDialog.Actions.PrimaryButton closeOnSubmit />
        </TabbedDialog.Actions>
    </TabbedDialog.Root>
 */
export default {
    Root: TabbedRoot,
    Title: TabbedHeader,
    Content: TabbedContent,
    Actions,
}
